/**
 * Horizontal bars comparing one magnitude across labelled rows.
 *
 * A single measure over categories, so the colour job is sequential: one hue for every bar.
 * Rank is never encoded in colour — a row that needs attention is called out in words next to
 * it instead. Each bar is directly labelled with its value, so the chart reads without a
 * legend and without hovering.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

/** One row of the chart. */
export interface BarRow {
  /** Stable key and row label. */
  id: string;
  label: string;
  /** Magnitude in `[0, 1]`, or `null` when there is no data for this row. */
  value: number | null;
  /** Value rendered at the end of the row, such as `86%`. */
  display: string;
  /** Optional short note shown under the label, for example a warning. */
  note?: { text: string; tone: 'warning' | 'muted' };
  /** Optional in-app destination; when set the whole row becomes a link. */
  to?: string;
}

/** Height of a bar in pixels. Thin marks keep the row list scannable. */
const BAR_HEIGHT = 8;

/**
 * Grid used by every row: label above the bar on a phone, beside it on a wide screen.
 *
 * The label column is capped rather than proportional. Left to take half the width it opens a
 * gap between a short name and the start of its bar, and the eye has to cross it on every row.
 */
const ROW_LAYOUT = 'grid grid-cols-1 gap-2 px-4 py-3'
  + ' sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)] sm:items-center sm:gap-6';

/** Renders the label, bar, and value of one row. */
function RowBody({ row }: { row: BarRow }): ReactNode {
  return (
    <>
      <div className="min-w-0">
        <p className="truncate text-row font-medium">{row.label}</p>
        {row.note === undefined ? null : (
          <p className={`text-tiny ${row.note.tone === 'warning' ? 'text-missing-text' : 'text-ink-3'}`}>
            {row.note.text}
          </p>
        )}
      </div>
      <div className="flex items-center gap-3">
        <div
          className="h-2 min-w-24 flex-1 overflow-hidden rounded-full bg-sunken"
          style={{ height: BAR_HEIGHT }}
        >
          {row.value === null ? null : (
            <div
              className="h-full rounded-full bg-brand-graphic"
              style={{ width: `${Math.max(2, row.value * 100)}%` }}
            />
          )}
        </div>
        <span className="tabular w-14 shrink-0 text-right text-row font-semibold">{row.display}</span>
      </div>
    </>
  );
}

/**
 * Renders the rows.
 *
 * @param rows already in the order they should appear; this component does not sort.
 * @param caption sentence describing what the bars measure, rendered under the chart.
 */
export function BarRows({ rows, caption }: { rows: readonly BarRow[]; caption: string }): ReactNode {
  if (rows.length === 0) return <p className="px-4 py-6 text-ink-2">Nothing to compare yet.</p>;

  return (
    <figure className="m-0">
      <ul>
        {rows.map((row) => (
          <li key={row.id} className="border-b border-line">
            {row.to === undefined ? (
              <div className={ROW_LAYOUT}>
                <RowBody row={row} />
              </div>
            ) : (
              <Link to={row.to} className={`${ROW_LAYOUT} hover:bg-sunken`}>
                <RowBody row={row} />
              </Link>
            )}
          </li>
        ))}
      </ul>
      <figcaption className="px-4 py-3 text-tiny text-ink-2">{caption}</figcaption>
    </figure>
  );
}
